import { motion } from 'framer-motion';
import { Nav } from './Nav';
import { Footer } from './Footer';

export const RefundPolicy = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <div className="relative overflow-hidden">
        <Nav />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
      
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="text-center py-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold leading-tight">
            Refund Policy
          </h1>
          <p className="mt-4 text-lg max-w-2xl mx-auto">
            <strong>Our Commitment:</strong> We want you to be happy with our service. This policy outlines when we offer refunds and how to request one. We aim for a fair and straightforward process.
          </p>
        </motion.div>

        {/* Content Sections */}
        <div className="space-y-10">
          {/* Section 1: 14-Day Money-Back Guarantee */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.2, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold mb-4">
              Subscription Refunds
            </h2>
            <p>
              We offer refunds on a case-by-case basis, guided by the principles below.
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2">
              <li>
                <strong>Annual Subscriptions:</strong> We offer a <strong>30-day money-back guarantee</strong> on all new annual subscription plans. If you are not satisfied with our service for any reason within the first 30 days of your purchase, you can request a full refund. This guarantee applies only to your first subscription.
              </li>
              <li>
                <strong>Monthly Subscriptions:</strong> For monthly subscriptions, we offer a refund if you cancel within <strong>72 hours</strong> of your initial purchase. After this 72-hour window, we do not offer refunds for monthly subscription payments. When you cancel a monthly plan, you will retain full access to the service until the end of that billing period and will not be charged again.
              </li>
              <li>
                <strong>Subscription Renewals:</strong> We do <strong>not</strong> offer refunds for automatic subscription renewals (either monthly or annual). To ensure you are not charged for a renewal you do not want, we will send a reminder email 7 days before your renewal date. You can cancel your subscription at any time from your account settings.
              </li>
            </ul>
           </motion.section>

          {/* Section 2: Subscription Renewals */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.3, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold mb-4">
              How to Request a Refund
            </h2>
            <p>
              Requesting a refund is simple. Please follow these steps:
            </p>
            <ul className="list-decimal list-inside mt-4 space-y-2">
              <li>
                <strong>Contact Support:</strong> Send an email to our support team at <a href="mailto:info@gomens.ai" className="text-indigo-600 hover:text-indigo-500 underline">info@gomens.ai</a> with the subject line "Refund Request."
              </li>
              <li>
                <strong>Provide Details:</strong> In your email, please include the email address associated with your account and a brief reason for the refund request. This helps us improve our service.
              </li>
              <li>
                <strong>Await Confirmation:</strong> We will review your request and get back to you within 2 business days.
              </li>
            </ul>
          </motion.section>

          {/* Section 3: Prorated Refunds and Downgrades */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.4, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold mb-4">
              Refund Processing
            </h2>
            <p>
              Once your refund request is approved, we will process it immediately through our payment processor, Stripe. Please note that it may take 5-10 business days for the funds to appear on your original method of payment, depending on your bank or card issuer.
            <br/>
            You will receive an email confirmation once the refund has been initiated on our end.
            </p>
          </motion.section>

          {/* Section 4: Exceptions */}
          <motion.section
            initial="hidden"
            animate="visible"
            variants={{
              ...fadeIn,
              transition: { delay: 0.5, duration: 0.6 },
            }}
            className="bg-white shadow-lg rounded-lg p-6"
          >
            <h2 className="text-2xl font-semibold mb-4">
              Important Details & Exceptions
            </h2>
            <ul className="list-disc list-inside mt-4 space-y-2">
              <li>
                <strong>Non-Refundable Charges:</strong> Certain fees are non-refundable. These include any one-time setup fees, data import services, or other professional services that have already been rendered.
              </li>
              <li>
                <strong>Chargebacks:</strong> Before filing a chargeback with your bank, we kindly ask that you contact us first to resolve any issues. We find that most problems can be solved with a simple conversation. Filing a chargeback for a valid charge is a violation of our terms and may result in the permanent suspension of your account.
              </li>
              <li>
                <strong>Policy Changes:</strong> We reserve the right to modify this Refund Policy at any time. Any changes will be posted on this page. Your continued use of our services after a change in policy will constitute your acceptance of those changes.
              </li>              
            </ul>
            <br />
            <p>If you have any questions about our refund policy, please don't hesitate to <a href="mailto:info@gomens.ai" className="text-indigo-600 hover:text-indigo-500 underline">contact us</a>.</p>
          </motion.section>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 -mt-20 -mr-20 hidden lg:block">
          <svg width="404" height="404" fill="none" viewBox="0 0 404 404">
            <defs>
              <pattern
                id="pattern-circles"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="2"
                  fill="rgba(124, 58, 237, 0.1)"
                />
              </pattern>
            </defs>
            <rect width="404" height="404" fill="url(#pattern-circles)" />
          </svg>
        </div>
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 hidden lg:block">
          <svg width="404" height="404" fill="none" viewBox="0 0 404 404">
            <defs>
              <pattern
                id="pattern-circles-2"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="2"
                  fill="rgba(124, 58, 237, 0.1)"
                />
              </pattern>
            </defs>
            <rect width="404" height="404" fill="url(#pattern-circles-2)" />
          </svg>
        </div>
        
      </div>
      <Footer />
    </div>
  );
};